import {
    Link,
    useSearchParams,
    useNavigate,
    useNavigation,
} from '@remix-run/react';
import {
    TileGrid,
    useStyles,
    useAppDisplayModeContext,
    Button,
    Flex,
    HotButtons,
} from '@archipro-design/aria';

import * as S from './FeaturedProjects.style';
import type ProjectTileItem from '@modules/tile/type/project-tile-item';
import { useTracker } from '@archipro-website/tracker';
import { useSaveToDesignBoard } from '~/modules/design-board/hook';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import { useAppData } from '~/modules/root/hook/use-app-data';
import { getHotButtons } from '~/modules/directory/util/get-hot-buttons';
import { visitBMEditor } from '~/modules/directory/util/tile-helpers';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';
import { getAdminCaption } from '~/modules/directory/util/get-admin-caption';
import { useDeleteItemWithConfirm } from '~/modules/directory/hook/use-delete-item-with-confirm';
import AdminCaption from '~/modules/tile/component/admin-caption/AdminCaption';
import ProjectTile from '~/modules/tile/component/project-tile/ProjectImpressionTile';
import { useToggleHomePin } from '~/modules/home/hook/use-toggle-home-pin';
import { useState } from 'react';
import MessageModal from '~/modules/home/component/common/MessageModal';
import { canHomePin } from '~/modules/home/util/home-pin-helpers';

interface FeaturedProjectsData {
    FeaturedProjects: {
        pageInfo: {
            totalCount: number;
        };
        edges: {
            node: ProjectTileItem;
        }[];
    };
}

export interface FeaturedProjectsProps {
    data: FeaturedProjectsData & {
        productTitle: string;
    };
}

const FeaturedProjectTiles = ({
    data: { FeaturedProjects, productTitle },
}: FeaturedProjectsProps) => {
    const { css } = useStyles();
    const appDisplayMode = useAppDisplayModeContext();
    const { mobile } = appDisplayMode;
    const [searchParams] = useSearchParams();
    const navigation = useNavigation();
    const tracker = useTracker();
    const { favouriteItem } = useSaveToDesignBoard('ProjectPin');
    const navigate = useNavigate();

    const { edges, pageInfo } = FeaturedProjects;

    const { rootGrants } = useAppData();
    const canEditAdmin = rootGrants.editProfile;
    const {
        itemID: itemIDToDelete,
        willDelete,
        confirmDelete,
        cancelDelete,
    } = useDeleteItemWithConfirm();

    const [pinErrorMessage, setPinErrorMessage] = useState<string | null>(null);
    const { toggleHomePin } = useToggleHomePin(canEditAdmin, (error) => {
        if (error.Message) {
            setPinErrorMessage(error.Message);
        }
    });

    if (!edges?.length) return null;

    const featuredProjectsCount = pageInfo.totalCount;

    const hasLimitApplied = searchParams.has('featuredProjectsShowAll');
    const nextSearchParams = new URLSearchParams(navigation.location?.search);
    const featuredProjectsShowAll =
        nextSearchParams.get('featuredProjectsShowAll') === '0';
    const isLoading = navigation.state === 'loading' && featuredProjectsShowAll;

    const TILES_DISPLAYED = 3;
    const showAllContent = !isLoading ? `View more` : 'Loading...';
    const canLoadMore =
        !hasLimitApplied && featuredProjectsCount > TILES_DISPLAYED;

    const featuredProjects =
        mobile && !hasLimitApplied ? edges.slice(0, 3) : edges;

    return (
        <div className={css(S.FeaturedProjectsWrapper)}>
            <TileGrid
                heading={{
                    children: (
                        <>
                            Projects featuring
                            <br />
                            {productTitle}
                        </>
                    ),
                    variables: {
                        fontWeight: 500,
                    },
                }}
                columns={1}
            >
                {featuredProjects.map(({ node }) => {
                    const hotButtons = getHotButtons({
                        item: node,
                        itemType: 'project',
                        appDisplayMode,
                        canEditAdmin,
                        canEdit: node.canEdit ?? false,
                        onEditClick: visitBMEditor,
                        onDeleteClick: () => willDelete(node.ID),
                        pinned: !!node.admin?.homePinned,
                        onPinClick: canHomePin(rootGrants)
                            ? () => {
                                  if (!node.admin) {
                                      return;
                                  }
                                  toggleHomePin(
                                      Number(node.ID),
                                      !node.admin.homePinned
                                  );
                              }
                            : undefined,
                    });
                    const adminCaption = getAdminCaption({
                        appDisplayMode,
                        item: node.admin,
                        canEditAdmin,
                    });

                    return (
                        <ProjectTile
                            data-testid="project-tile"
                            key={`featured-project-tile-${node.ID}`}
                            as={Link}
                            to={node.Link}
                            variant={mobile ? '02' : '01'}
                            newDesignEnabled={true}
                            size={'large'}
                            onClick={() => {
                                tracker.log('ProductProjectClick', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: {
                                        ExtraData: node.ID,
                                    },
                                });
                            }}
                            onFavourite={(e) =>
                                favouriteItem.onClick(e, node.ID)
                            }
                            isFavourited={favouriteItem.favourited(node.ID)}
                            topActions={
                                hotButtons ? (
                                    <HotButtons items={hotButtons} />
                                ) : undefined
                            }
                            adminCaption={
                                adminCaption ? (
                                    <AdminCaption adminCaption={adminCaption} />
                                ) : undefined
                            }
                            image={[
                                {
                                    src: node.TileThumbUrl || '',
                                    alt: node.Title,
                                },
                            ]}
                            projectName={{
                                children: node.Title,
                                onClick: () => navigate(node.Link),
                            }}
                            professionalName={node.Professional?.Title}
                        />
                    );
                })}
            </TileGrid>

            {canLoadMore && (
                <Flex className={css(S.LoadMoreButtonWrapper)}>
                    <Button
                        as={Link}
                        to={'?featuredProjectsShowAll=0'}
                        preventScrollReset={true}
                        loading={isLoading}
                        prefetch="intent"
                        className={css(S.LoadMoreButton)}
                        size={16}
                        style={{
                            textTransform: isLoading ? 'unset' : 'uppercase',
                        }}
                        outlined={true}
                    >
                        {showAllContent}
                    </Button>
                </Flex>
            )}

            {favouriteItem.target && (
                <SaveToDesignBoard {...favouriteItem} autoReposition={false} />
            )}
            {itemIDToDelete && (
                <DeleteItemConfirmModal
                    title={'Delete project'}
                    content={
                        'Are you sure you would like to delete this project? All information and associated tags will be lost.'
                    }
                    open={itemIDToDelete > 0}
                    onCancel={cancelDelete}
                    onConfirm={confirmDelete}
                />
            )}
            {pinErrorMessage && (
                <MessageModal
                    title={'Warning'}
                    content={pinErrorMessage}
                    open={true}
                    onConfirm={() => setPinErrorMessage(null)}
                />
            )}
        </div>
    );
};

export default FeaturedProjectTiles;
